import { useEffect, useState } from "react";
import client, { apiError } from "../api/client";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatDateFull } from "../utils/format";
import { Bell, Loader2, CheckCircle2, XCircle, Send } from "lucide-react";

const OFFSET_LABEL = { 7: "7 dní předem", 3: "3 dny předem", 1: "1 den předem" };

export default function Notifications() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all | sent | failed
  const canTrigger = user?.role === "admin" || user?.role === "user";
  const isAdmin = user?.role === "admin";
  const [config, setConfig] = useState({ summary_email: "" });
  const [savingCfg, setSavingCfg] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);

  const load = () => client.get("/notifications/history").then((res) => setData(res.data));
  useEffect(() => {
    load();
    if (user?.role === "admin") client.get("/admin/config").then((res) => setConfig(res.data));
  }, [user]);

  const saveConfig = async () => {
    setSavingCfg(true);
    try { await client.put("/admin/config", { summary_email: config.summary_email }); toast.success("Souhrnný e-mail uložen"); }
    catch (err) { toast.error(apiError(err)); } finally { setSavingCfg(false); }
  };
  const sendSummary = async () => {
    setSendingSummary(true);
    try {
      const r = await client.post("/notifications/send-summary");
      toast.success(r.data.summary_sent ? `Souhrn odeslán na ${r.data.recipient}` : "Souhrn neodeslán – chybí příjemce");
    } catch (err) { toast.error(apiError(err)); } finally { setSendingSummary(false); }
  };

  const trigger = async () => {
    setLoading(true);
    try {
      const res = await client.post("/notifications/trigger");
      toast.success(`Zkontrolováno ${res.data.checked} workshopů, odesláno ${res.data.sent} připomínek`);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const items = (data?.items || []).filter((n) =>
    filter === "all" ? true : filter === "sent" ? n.success : !n.success
  );

  const cards = [
    { label: "Odesláno celkem", value: data?.sent ?? 0, cls: "text-emerald-700" },
    { label: "Neodesláno (chyby)", value: data?.failed ?? 0, cls: "text-destructive" },
    { label: "Záznamů v přehledu", value: data?.items?.length ?? 0, cls: "" },
  ];

  return (
    <>
      <div className="h-16 border-b border-border px-8 flex items-center justify-between sticky top-0 bg-background/80 backdrop-blur-md z-10">
        <div>
          <h1 className="font-heading text-xl font-semibold tracking-tight">Notifikace lektorům</h1>
          <p className="text-xs text-muted-foreground">Automaticky každý den v 7:00 (7, 3 a 1 den před akcí). Přehled odeslaných i neúspěšných.</p>
        </div>
        {canTrigger && (
          <button onClick={trigger} disabled={loading} data-testid="trigger-notifications" className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-60 flex items-center gap-1.5">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />} Spustit připomínky teď
          </button>
        )}
      </div>

      <div className="p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="notif-summary">
          {cards.map((c) => (
            <div key={c.label} className="border border-border rounded-lg p-5 bg-card">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{c.label}</div>
              <div className={`font-heading text-3xl font-semibold tracking-tight ${c.cls}`}>{c.value}</div>
            </div>
          ))}
        </div>

        {isAdmin && (
          <div className="border border-border rounded-lg p-5 bg-card" data-testid="summary-config">
            <div className="flex items-center gap-2 mb-1"><Send className="h-4 w-4 text-muted-foreground" /><h3 className="font-medium text-sm">Ranní souhrn e-mailem</h3></div>
            <p className="text-xs text-muted-foreground mb-3">Každé ráno v 7:00 na tento e-mail dorazí přehled, co se odeslalo a co ne.</p>
            <div className="flex gap-2 flex-wrap">
              <input
                data-testid="summary-email-input"
                value={config.summary_email || ""}
                onChange={(e) => setConfig((c) => ({ ...c, summary_email: e.target.value }))}
                placeholder="lucie@digitalnimost.cz"
                className="flex-1 min-w-[220px] h-9 px-3 rounded-md border border-input bg-background text-sm outline-none focus:ring-2 focus:ring-ring/20"
              />
              <button onClick={saveConfig} disabled={savingCfg} data-testid="summary-save" className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-60">Uložit</button>
              <button onClick={sendSummary} disabled={sendingSummary} data-testid="summary-test" className="h-9 px-4 rounded-md border border-border text-sm hover:bg-secondary disabled:opacity-60 flex items-center gap-1.5">{sendingSummary ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Poslat souhrn teď</button>
            </div>
          </div>
        )}

        <div className="flex rounded-md border border-border overflow-hidden w-fit">
          {[["all", "Vše"], ["sent", "Odesláno"], ["failed", "Neodesláno"]].map(([k, l]) => (
            <button key={k} data-testid={`notif-filter-${k}`} onClick={() => setFilter(k)} className={`h-8 px-4 text-sm ${filter === k ? "bg-secondary font-medium" : "hover:bg-secondary/50 text-muted-foreground"}`}>{l}</button>
          ))}
        </div>

        {!data ? (
          <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Načítání…</div>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="notifications-history">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border bg-muted/40">
                  <th className="text-left font-medium px-3 py-2">Stav</th>
                  <th className="text-left font-medium px-3 py-2">Lektor (e-mail)</th>
                  <th className="text-left font-medium px-3 py-2">Workshop</th>
                  <th className="text-left font-medium px-3 py-2">Předstih</th>
                  <th className="text-left font-medium px-3 py-2">Odesláno</th>
                </tr>
              </thead>
              <tbody>
                {items.map((n, i) => (
                  <tr key={i} className="border-b border-border/60" data-testid={`notif-row-${i}`}>
                    <td className="px-3 py-2">
                      {n.success ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> Odesláno</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-destructive text-xs"><XCircle className="h-3.5 w-3.5" /> Chyba</span>
                      )}
                    </td>
                    <td className="px-3 py-2">{n.lector_email}</td>
                    <td className="px-3 py-2">{n.school_name} <span className="text-muted-foreground">{n.workshop_date ? `· ${formatDateFull(n.workshop_date)}` : ""}</span></td>
                    <td className="px-3 py-2 text-muted-foreground">{n.manual ? "Ručně odesláno" : (OFFSET_LABEL[n.offset] || `${n.offset} dní`)}</td>
                    <td className="px-3 py-2 text-muted-foreground">{(n.sent_at || "").slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">Žádné záznamy</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
